import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useCreateVenuaPlannerTermTransportSchemeOverride,
  useRemoveVenuaPlannerTermTransportSchemeOverride,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { invalidateCalculationScopes, invalidateTransportSchemes } from '../useChangeLayerState';

interface Options {
  productId: string;
  serieId: string;
  termId: string;
  onDone: () => void;
}

/**
 * Přepnutí termínu mezi sdílenou definicí seriálu a vlastní.
 *
 * Klon přebírá efektivní hodnoty termínu, takže se čísla nezmění, a přiřazená linka zůstává — patří
 * termínu, ne schématu. Draft se po přepnutí odvodí znovu (klíč `schemeId + isOwnScheme`).
 */
export function useSetTermTransportSchemeOwnership({
  productId,
  serieId,
  termId,
  onDone,
}: Options) {
  const queryClient = useQueryClient();
  const create = useCreateVenuaPlannerTermTransportSchemeOverride();
  const remove = useRemoveVenuaPlannerTermTransportSchemeOverride();

  return useCallback(
    (useOwn: boolean) => {
      const run = useOwn
        ? create.mutateAsync({ productId, serieId, termId })
        : remove.mutateAsync({ productId, serieId, termId });

      void run
        .then(async () => {
          // Přepnutí vymění efektivní definici za jinou — klon má **vlastní ID ticketů i směrů**.
          // Bez invalidace by editor dál držel draft té původní a uložení by poslalo cizí ID ticketů,
          // na kterých server skončí konfliktem. Seriálový pohled zase ukazuje u řádků termínů
          // příznak vlastní dopravy. Invalidace musí proběhnout dřív než `onDone`, aby volající
          // dostal řízení až nad čerstvými daty.
          await invalidateTransportSchemes(queryClient, { productId, serieId });
          // Kalkulace nese u termínu příznak vlastní dopravy a náklad dopravy počítá z jeho schématu —
          // po výměně definice se musí přečíst znovu, i když efektivní ceny zůstaly stejné.
          invalidateCalculationScopes(queryClient, { productId, serieId });
          onDone();
        })
        .catch(() => undefined);
    },
    [create, onDone, productId, queryClient, remove, serieId, termId]
  );
}
